import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { Toaster } from 'sonner'
import { AuthProvider } from './hooks/useAuth'

interface ProvidersProps {
  children: ReactNode
}

function ErrorFallback({
  error,
  resetErrorBoundary,
}: {
  error: Error
  resetErrorBoundary: () => void
}) {
  return (
    <div className="alert alert-error" role="alert" style={{ margin: 24 }}>
      <p className="mb-8">Unexpected error</p>
      <pre>{error.message}</pre>
      <button className="btn btn-secondary mt-8" onClick={resetErrorBoundary}>
        Reload UI
      </button>
    </div>
  )
}

export function Providers({ children }: ProvidersProps) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 15_000,
            refetchOnWindowFocus: false,
            retry: (failureCount, error) => {
              const status = (error as { status?: number } | undefined)?.status
              if (status && status >= 400 && status < 500) return false
              return failureCount < 2
            },
          },
          mutations: {
            retry: false,
          },
        },
      }),
  )

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          {children}
          <Toaster richColors position="top-right" theme="dark" />
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  )
}
